import React, { useEffect, useMemo } from 'react';
import SrtBarGraph from './SrtBarGraph';
import WrsLineGraph from './WrsLineGraph';
import PbMaxBarGraph from './PbMaxBarGraph';
import {
  TEST_MATERIAL_OPTIONS, PRESENTATION_OPTIONS, ENV_OPTIONS, TRANSDUCER_OPTIONS,
  normaliseSpeech, setSRT, setWRS, setPBMax, addWrsIntensityRow, removeWrsIntensityRow,
  autoPbMax, wrsClass, buildClinicalInterpretation, buildRecommendations,
} from './speechLogic';

const RE_TEXT = 'text-red-700';
const LE_TEXT = 'text-blue-700';

const SpeechDataEntry = ({ data, onChange, audiologistName, printPreview = false }) => {
  const state = useMemo(() => normaliseSpeech(data), [data]);
  const [wrsEar, setWrsEar] = React.useState('right');
  const [newRowDb, setNewRowDb] = React.useState('');

  const setField = (k, v) => onChange({ ...state, [k]: v });

  useEffect(() => {
    if (!state.clinician && audiologistName) setField('clinician', audiologistName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numInput = (val, onChangeVal, testId, placeholder = '—', step = 5, min = null, max = null) => (
    <input type="number" step={step}
      value={val === undefined || val === null ? '' : val}
      min={min ?? undefined} max={max ?? undefined}
      disabled={printPreview}
      onChange={(e) => onChangeVal(e.target.value === '' ? null : Number(e.target.value))}
      className={`w-full px-1 py-0.5 text-center bg-transparent focus:outline-none focus:bg-yellow-50 ${printPreview ? 'text-slate-700' : ''}`}
      data-testid={testId} placeholder={placeholder} />
  );

  const pbR = autoPbMax(state, 'right');
  const pbL = autoPbMax(state, 'left');
  const clsR = wrsClass(pbR);
  const clsL = wrsClass(pbL);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-auto p-3" data-testid="speech-data-entry">
      {/* Common fields */}
      <div className="grid grid-cols-4 gap-2 mb-3 bg-white border border-slate-200 rounded-md p-2" data-testid="speech-common-fields">
        <label className="text-xs col-span-2">
          <span className="block text-slate-600 font-semibold mb-0.5">Test Material</span>
          <select value={state.test_material} onChange={(e) => setField('test_material', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="speech-test-material">
            {TEST_MATERIAL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Presentation</span>
          <select value={state.presentation} onChange={(e) => setField('presentation', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="speech-presentation">
            {PRESENTATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Standard</span>
          <input type="text" value={state.standard} onChange={(e) => setField('standard', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid="speech-standard" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Test Environment</span>
          <select value={state.environment} onChange={(e) => setField('environment', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="speech-environment">
            {ENV_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Transducer</span>
          <select value={state.transducer} onChange={(e) => setField('transducer', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="speech-transducer">
            {TRANSDUCER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Clinician</span>
          <input type="text" value={state.clinician} onChange={(e) => setField('clinician', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="Auto-filled" data-testid="speech-clinician" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Refer by</span>
          <input type="text" value={state.refer_by} onChange={(e) => setField('refer_by', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="Self / referring physician" data-testid="speech-refer-by" />
        </label>
      </div>

      {/* Section 1 · SRT */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="srt-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">1 · Speech Reception Threshold (SRT)</h3>
        <div className="text-[10px] text-slate-500 mb-1">Lowest intensity at which the patient correctly identifies 50% of spondee words. Drag the bar or type in the table — both stay in sync.</div>
        <div className={printPreview ? 'max-w-[340px] mx-auto' : 'max-w-[640px] mx-auto'}>
          <SrtBarGraph
            srt={state.srt}
            onSetValue={(ear, db) => onChange(setSRT(state, ear, db))}
            readOnly={printPreview}
            width={printPreview ? 330 : 460}
            height={printPreview ? 190 : 240}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="srt-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Ear</th>
                <th className="border border-slate-300 px-1 py-0.5">SRT (dB HL)</th>
              </tr>
            </thead>
            <tbody>
              {['right', 'left'].map((ear, i) => (
                <tr key={ear} className={i % 2 ? 'bg-slate-50' : ''}>
                  <td className={`border border-slate-300 px-1 py-0.5 font-bold ${ear === 'right' ? RE_TEXT : LE_TEXT}`}>{ear === 'right' ? 'Right Ear (RE)' : 'Left Ear (LE)'}</td>
                  <td className="border border-slate-300 p-0">
                    {numInput(state.srt[ear], (v) => onChange(setSRT(state, ear, v)), `srt-input-${ear}`, 'e.g. 20', 5, 0, 120)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2 · WRS */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="wrs-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">2 · Word Recognition Score (WRS)</h3>
        <div className="text-[10px] text-slate-500 mb-1">Percentage of words correctly identified at supra-threshold levels. Click on the graph to plot a score at any intensity — or type % directly in the table.</div>
        <div className={printPreview ? 'max-w-[350px] mx-auto' : 'max-w-[640px] mx-auto'}>
          <WrsLineGraph
            wrs={state.wrs}
            intensities={state.wrs_rows}
            activeEar={wrsEar}
            onSetEar={setWrsEar}
            onSetValue={(ear, db, pct) => onChange(setWRS(state, ear, db, pct))}
            readOnly={printPreview}
            width={printPreview ? 340 : 460}
            height={printPreview ? 190 : 240}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="wrs-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5 w-1/3">Intensity (dB HL)</th>
                <th className={`border border-slate-300 px-1 py-0.5 ${RE_TEXT}`}>Right Ear (%)</th>
                <th className={`border border-slate-300 px-1 py-0.5 ${LE_TEXT}`}>Left Ear (%)</th>
                <th className="border border-slate-300 px-1 py-0.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {state.wrs_rows.map((db, i) => (
                <tr key={db} className={i % 2 ? 'bg-slate-50' : ''}>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{db}</td>
                  <td className="border border-slate-300 p-0">
                    {numInput(state.wrs.right[String(db)], (v) => onChange(setWRS(state, 'right', db, v)), `wrs-right-${db}`, 'e.g. 90', 2, 0, 100)}
                  </td>
                  <td className="border border-slate-300 p-0">
                    {numInput(state.wrs.left[String(db)], (v) => onChange(setWRS(state, 'left', db, v)), `wrs-left-${db}`, 'e.g. 90', 2, 0, 100)}
                  </td>
                  <td className="border border-slate-300 text-center">
                    <button type="button" onClick={() => onChange(removeWrsIntensityRow(state, db))}
                      className="text-[10px] text-slate-400 hover:text-rose-600 px-1" data-testid={`wrs-remove-${db}`} title="Remove this row">×</button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={4} className="border border-slate-300 px-1 py-1 bg-slate-50">
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-slate-500 font-semibold">Add row:</span>
                    <input type="number" step="5" value={newRowDb} onChange={(e) => setNewRowDb(e.target.value)}
                      className="w-20 px-1 py-0.5 border border-slate-300 rounded text-center"
                      placeholder="dB HL" data-testid="wrs-new-row-db" />
                    <button type="button"
                      onClick={() => {
                        const db = Number(newRowDb);
                        if (Number.isFinite(db)) { onChange(addWrsIntensityRow(state, db)); setNewRowDb(''); }
                      }}
                      className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded font-bold hover:bg-sky-200"
                      data-testid="wrs-add-row">+ Add</button>
                    <span className="ml-auto text-slate-400 italic">RE peak: {pbR ?? '—'}% {clsR ? `(${clsR})` : ''} · LE peak: {pbL ?? '—'}% {clsL ? `(${clsL})` : ''}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3 · PB Max */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="pbmax-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">3 · Speech Discrimination (PB Max)</h3>
        <div className="text-[10px] text-slate-500 mb-1">Best word recognition score achieved across all intensity levels. Auto-populates from the highest WRS value per ear — override by typing in the table or dragging the bar.</div>
        <div className={printPreview ? 'max-w-[230px] mx-auto' : 'max-w-[640px] mx-auto'}>
          <PbMaxBarGraph
            state={state}
            onSetValue={(ear, pct) => onChange(setPBMax(state, ear, pct))}
            readOnly={printPreview}
            width={printPreview ? 220 : 460}
            height={printPreview ? 180 : 240}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="pbmax-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Ear</th>
                <th className="border border-slate-300 px-1 py-0.5">Score (%)</th>
                <th className="border border-slate-300 px-1 py-0.5 w-1/3">Source</th>
              </tr>
            </thead>
            <tbody>
              {['right', 'left'].map((ear, i) => {
                const override = state.pb_max[ear];
                const auto = autoPbMax(state, ear);
                const displayed = (override !== null && override !== undefined) ? override : auto;
                return (
                  <tr key={ear} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className={`border border-slate-300 px-1 py-0.5 font-bold ${ear === 'right' ? RE_TEXT : LE_TEXT}`}>{ear === 'right' ? 'Right Ear (RE)' : 'Left Ear (LE)'}</td>
                    <td className="border border-slate-300 p-0">
                      {numInput(displayed, (v) => onChange(setPBMax(state, ear, v)), `pbmax-input-${ear}`, 'auto', 2, 0, 100)}
                    </td>
                    <td className="border border-slate-300 px-1 py-0.5 text-[9px] text-slate-500">
                      {(override !== null && override !== undefined)
                        ? <span>Override <button type="button" onClick={() => onChange(setPBMax(state, ear, null))} className="ml-1 text-sky-700 hover:underline" data-testid={`pbmax-clear-${ear}`}>Clear</button></span>
                        : (auto !== null && auto !== undefined ? `Auto from WRS peak (${auto}%)` : '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interpretation + Recommendations */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="border border-slate-200 rounded p-2 bg-white">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-bold uppercase text-slate-600">Clinical Interpretation</div>
            <button type="button" onClick={() => setField('clinical_interpretation', buildClinicalInterpretation(state))}
              className="text-[9px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded hover:bg-sky-200" data-testid="speech-auto-fill-clin">Auto-fill</button>
          </div>
          <textarea value={state.clinical_interpretation} onChange={(e) => setField('clinical_interpretation', e.target.value)} rows={6}
            placeholder={"• Speech Reception Threshold (SRT) is in agreement with pure-tone thresholds.\n• Word recognition scores are excellent in the right ear and good-to-excellent in the left ear.\n• No evidence of significant rollover observed.\n• Results suggest normal to near-normal speech understanding at supra-threshold levels."}
            className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y" data-testid="speech-clinical-interpretation" />
        </div>
        <div className="border border-slate-200 rounded p-2 bg-white">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-bold uppercase text-slate-600">Recommendations</div>
            <button type="button" onClick={() => setField('recommendations', buildRecommendations())}
              className="text-[9px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded hover:bg-sky-200" data-testid="speech-auto-fill-rec">Auto-fill</button>
          </div>
          <textarea value={state.recommendations} onChange={(e) => setField('recommendations', e.target.value)} rows={6}
            placeholder={"• Correlate with pure-tone audiometry and case history.\n• Consider amplification evaluation if communicative difficulties are reported.\n• Continue audiological monitoring as advised."}
            className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y" data-testid="speech-recommendations" />
        </div>
      </div>
    </div>
  );
};

export default SpeechDataEntry;
